import React, { ReactNode } from 'react'
import styled from 'styled-components'
import { AuthOnboardingWidePageContainer, AuthHeaderBlock, AuthTitleBlock, AuthTitle, AuthSubtitleLarge, FullLogo } from '../../authentication/authFormCommon'
import AuthLegalFooter from '../../authentication/components/AuthLegalFooter'
import OnboardingStepper, { ONBOARDING_STEPPER_STEPS, OnboardingStepperStep } from './OnboardingStepper'
import { ONBOARDING_CARD_WIDTH, ONBOARDING_STEPPER_BREAKPOINT, onboardingPageWidth } from '../onboardingLayoutConstants'

interface IOnboardingStepLayoutProps {
    activeStepIndex: number
    /** Card / bordered box — stepper aligns with this row. */
    children: ReactNode
    /** Page heading below the logo. */
    title?: string
    /** Optional subtitle below the title. */
    subtitle?: string
    /** Main column width — defaults to 650px (Figma). */
    contentMaxWidth?: number
    /** Hide right-side stepper (e.g. All Set without extension). */
    showStepper?: boolean
    /** Override stepper steps (e.g. skip flow without extension). */
    steps?: OnboardingStepperStep[]
}

const OnboardingStepLayout: React.FC<IOnboardingStepLayoutProps> = ({
    activeStepIndex,
    children,
    title,
    subtitle,
    contentMaxWidth = ONBOARDING_CARD_WIDTH,
    showStepper = true,
    steps = ONBOARDING_STEPPER_STEPS,
}) => (
    <AuthOnboardingWidePageContainer>
        <PageColumn $contentMaxWidth={contentMaxWidth} $showStepper={showStepper}>
            <HeaderColumn $maxWidth={contentMaxWidth}>
                <AuthHeaderBlock>
                    <FullLogo />
                    {(title || subtitle) && (
                        <AuthTitleBlock>
                            {title && <AuthTitle>{title}</AuthTitle>}
                            {subtitle && <AuthSubtitleLarge>{subtitle}</AuthSubtitleLarge>}
                        </AuthTitleBlock>
                    )}
                </AuthHeaderBlock>
            </HeaderColumn>

            <BoxRow>
                <BoxColumn $maxWidth={contentMaxWidth}>{children}</BoxColumn>
                {showStepper && (
                    <StepperColumn>
                        <OnboardingStepper steps={steps} activeStepIndex={activeStepIndex} />
                    </StepperColumn>
                )}
            </BoxRow>
        </PageColumn>
        <AuthLegalFooter />
    </AuthOnboardingWidePageContainer>
)

export default OnboardingStepLayout

const PageColumn = styled.div<{ $contentMaxWidth: number; $showStepper: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 28px;
    width: min(
        100%,
        ${({ $contentMaxWidth, $showStepper }) =>
            $showStepper ? onboardingPageWidth($contentMaxWidth) : $contentMaxWidth}px
    );
    max-width: 100%;
    margin: 0 auto;

    @media (max-width: ${ONBOARDING_STEPPER_BREAKPOINT}px) {
        width: min(100%, ${({ $contentMaxWidth }) => $contentMaxWidth}px);
    }
`

const HeaderColumn = styled.div<{ $maxWidth: number }>`
    width: 100%;
    max-width: ${({ $maxWidth }) => $maxWidth}px;
`

const BoxRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 48px;
    width: fit-content;
    max-width: 100%;
`

const BoxColumn = styled.div<{ $maxWidth: number }>`
    display: flex;
    flex-direction: column;
    flex: 0 0 auto;
    width: ${({ $maxWidth }) => $maxWidth}px;
    max-width: 100%;
`

const StepperColumn = styled.div`
    flex-shrink: 0;
    align-self: flex-start;

    @media (max-width: ${ONBOARDING_STEPPER_BREAKPOINT}px) {
        display: none;
    }
`
